import { Clock3, MessageCircleMore } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import type { ExperimentAssignment } from "../experiments/experimentClient";
import { formatDateTime, outcomeLabel, stageLabel } from "./monetizationModel";

export function MonetizationContactCard({
  assignment,
  onSelect,
}: {
  assignment: ExperimentAssignment;
  onSelect: (assignment: ExperimentAssignment) => void;
}) {
  const outcome = assignment.conversion_outcome;
  const isPaid = outcome === "paid";
  const paidOrders = assignment.metrics?.paid_orders ?? 0;
  // An abandoned cart is a customer who went quiet, not a payment that broke:
  // it reads as neutral, beside a declined card or a reversal that does not.
  const hasIssue =
    outcome === "payment_failed" ||
    outcome === "refunded" ||
    outcome === "disputed";

  return (
    <Button
      type="button"
      variant="ghost"
      className="h-auto w-full justify-start whitespace-normal rounded-md border bg-background p-3 text-left shadow-sm hover:bg-muted/60"
      onClick={() => onSelect(assignment)}
    >
      <span className="min-w-0 flex-1 space-y-2">
        <span className="flex items-start justify-between gap-2">
          <span className="truncate font-medium tabular-nums">
            {assignment.user_id}
          </span>
          <Badge
            variant={
              hasIssue ? "destructive" : isPaid ? "default" : "secondary"
            }
            className="shrink-0"
          >
            {isPaid && paidOrders > 1
              ? `Paid ×${paidOrders}`
              : outcomeLabel(outcome)}
          </Badge>
        </span>

        <span className="flex flex-wrap gap-1 text-xs text-muted-foreground">
          <span className="capitalize">
            {assignment.acquisition_surface ?? "Unknown source"}
          </span>
          <span>·</span>
          <span>{stageLabel(assignment.furthest_stage)}</span>
        </span>

        <span className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span className="flex min-w-0 items-center gap-1">
            <Clock3 className="size-3 shrink-0" />
            <span className="truncate">
              {formatDateTime(
                assignment.latest_event_at ?? assignment.assigned_at,
              )}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-1">
            <MessageCircleMore className="size-3" />
            Open
          </span>
        </span>
      </span>
    </Button>
  );
}
